/**
 * SyncStatusBar - Persistent banner for offline/sync status feedback.
 *
 * Displayed at the top of the app (inside OfflineProvider) to inform the
 * user about their connectivity state and any pending offline items.
 *
 * Visual states:
 *	 1. Online, no pending items   -> Hidden (no banner rendered)
 *	 2. Online, pending items	   -> Yellow bar: "{n} items waiting to sync" + "Sync Now" button
 *	 3. Offline					   -> Red bar: "You are offline. Changes will be saved locally."
 *	 4. Syncing					   -> Blue bar with spinner: "Syncing {n} items..."
 *	 5. Sync complete (no errors)  -> Green bar: "Sync complete! {n} items synced." (auto-hide 5s)
 *	 6. Sync with errors		   -> Orange bar: "Sync completed with {n} errors." + "Retry" button
 *
 * Uses brand colors via CSS custom properties and px-based Tailwind spacing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOfflineSync } from '../../../hooks/useOfflineSync';

/**
 * Props for the SyncStatusBar component.
 *
 * @property className - Optional outer className merged onto the root element
 */
export interface SyncStatusBarProps {
	className?: string;
}

/**
 * SyncStatusBar component — renders the appropriate banner based on the
 * current offline context state. Consumes OfflineContext via useOfflineSync().
 */
export const SyncStatusBar: React.FC<SyncStatusBarProps> = ({ className }) => {
	// Pull all offline/sync state from context
	const {
		isOnline,
		pendingCount,
		isSyncing,
		syncResults,
		triggerSync,
	} = useOfflineSync();

	// --- Auto-hide state for the "sync complete" banner ---
	// When sync finishes successfully with no errors, show the green bar
	// for 5 seconds then auto-hide it.
	const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(false);

	// Track the previous syncing state to detect sync completion
	const [wasSyncing, setWasSyncing] = useState<boolean>(false);

	/**
	 * Detect sync completion: when isSyncing transitions from true to false
	 * and there are results with no errors, show the success banner briefly.
	 */
	useEffect(() => {
		if (wasSyncing && !isSyncing && syncResults) {
			// Sync just finished — check if it was fully successful
			if (syncResults.failed === 0 && syncResults.synced > 0) {
				setShowSuccessBanner(true);

				// Auto-hide after 5 seconds
				const timer = setTimeout(() => {
					setShowSuccessBanner(false);
				}, 5000);

				return () => clearTimeout(timer);
			}
		}

		// Update the tracking ref
		setWasSyncing(isSyncing);
	}, [isSyncing, wasSyncing, syncResults]);

	/**
	 * handleSync() - Triggers a manual sync when the user clicks "Sync Now"
	 * or "Retry". Delegates to the context's triggerSync().
	 */
	const handleSync = useCallback(() => {
		triggerSync();
	}, [triggerSync]);

	// --- State 4: Currently syncing ---
	// Blue bar with a spinning indicator
	if (isSyncing) {
		return (
			<div className={['sync-status-bar', 'sync-status-bar--syncing', 'w-full bg-blue-600 text-white px-16px py-10px flex items-center justify-center gap-8px text-14px font-medium shadow-md z-50', className ?? ''].join(' ')} data-ref="organism-sync-status-bar">
				{/* Spinning animation for sync-in-progress indicator */}
				<svg className="sync-status-bar__icon animate-spin h-16px w-16px text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
					<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
					<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
				</svg>
				<span className="sync-status-bar__message">Syncing {pendingCount} item{pendingCount !== 1 ? 's' : ''}...</span>
			</div>
		);
	}

	// --- State 6: Sync completed with errors ---
	// Orange bar with error count and retry button
	if (syncResults && syncResults.failed > 0) {
		return (
			<div className={['sync-status-bar', 'sync-status-bar--error', 'w-full bg-orange-500 text-white px-16px py-10px flex flex-wrap items-center justify-between gap-8px text-14px font-medium shadow-md z-50', className ?? ''].join(' ')} data-ref="organism-sync-status-bar">
				<div className="sync-status-bar__body flex items-center gap-8px min-w-0">
					{/* Warning icon */}
					<svg className="sync-status-bar__icon h-16px w-16px text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
					</svg>
					<span className="sync-status-bar__message">Sync completed with {syncResults.failed} error{syncResults.failed !== 1 ? 's' : ''}.{syncResults.synced > 0 ? ` ${syncResults.synced} item${syncResults.synced !== 1 ? 's' : ''} synced.` : ''}</span>
				</div>
				<button type="button" onClick={handleSync} className="sync-status-bar__action bg-white text-orange-600 inline-flex items-center px-12px py-4px min-h-44px desktop:min-h-0 rounded-4px text-13px font-semibold hover:bg-orange-50 transition-colors">Retry</button>
			</div>
		);
	}

	// --- State 5: Sync completed successfully (auto-hide after 5s) ---
	// Green bar with success message
	if (showSuccessBanner && syncResults && syncResults.failed === 0) {
		return (
			<div className={['sync-status-bar', 'sync-status-bar--success', 'w-full bg-green-600 text-white px-16px py-10px flex items-center justify-center gap-8px text-14px font-medium shadow-md z-50', className ?? ''].join(' ')} data-ref="organism-sync-status-bar">
				{/* Checkmark icon */}
				<svg className="sync-status-bar__icon h-16px w-16px text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
				</svg>
				<span className="sync-status-bar__message">Sync complete! {syncResults.synced} item{syncResults.synced !== 1 ? 's' : ''} synced.</span>
			</div>
		);
	}

	// --- State 3: Offline ---
	// Red bar warning the user that they're disconnected
	if (!isOnline) {
		return (
			<div className={['sync-status-bar', 'sync-status-bar--offline', 'w-full bg-red-600 text-white px-16px py-10px flex items-center justify-center gap-8px text-14px font-medium shadow-md z-50', className ?? ''].join(' ')} data-ref="organism-sync-status-bar">
				{/* Offline / no-wifi icon */}
				<svg className="sync-status-bar__icon h-16px w-16px text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0" />
				</svg>
				<span className="sync-status-bar__message">You are offline. Changes will be saved locally.</span>
			</div>
		);
	}

	// --- State 2: Online with pending items ---
	// Yellow bar prompting the user to sync
	if (isOnline && pendingCount > 0) {
		return (
			<div className={['sync-status-bar', 'sync-status-bar--pending', 'w-full bg-yellow-500 text-yellow-900 px-16px py-10px flex flex-wrap items-center justify-between gap-8px text-14px font-medium shadow-md z-50', className ?? ''].join(' ')} data-ref="organism-sync-status-bar">
				<div className="sync-status-bar__body flex items-center gap-8px min-w-0">
					{/* Cloud upload icon */}
					<svg className="sync-status-bar__icon h-16px w-16px text-yellow-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
					</svg>
					<span className="sync-status-bar__message">You have {pendingCount} item{pendingCount !== 1 ? 's' : ''} waiting to sync</span>
				</div>
				<button type="button" onClick={handleSync} className="sync-status-bar__action bg-yellow-900 text-white inline-flex items-center px-12px py-4px min-h-44px desktop:min-h-0 rounded-4px text-13px font-semibold hover:bg-yellow-800 transition-colors">Sync Now</button>
			</div>
		);
	}

	// --- State 1: Online, no pending items ---
	// Nothing to show — return null to hide the banner
	return null;
};

export default SyncStatusBar;
